import {
  memo,
  type ReactElement,
  useEffect,
  useId,
  useMemo,
  useRef,
} from 'react'
import styled, { keyframes } from 'styled-components'

import { flowStrokeColor } from './flowStrokeColor'
import type { FlowPath } from './flowTypes'
import { IsolationMarkerLayer } from './IsolationMarkerLayer'
import type { IsolationHighlight, IsolationMarker } from './isolationMarkers'

const noMarkers: IsolationMarker[] = []
const noHighlights: IsolationHighlight[] = []

export type FlowAnimationLayerProps = {
  paths: FlowPath[]
  isolating?: boolean
  markers?: IsolationMarker[]
  highlights?: IsolationHighlight[]
}
function strokeColor({
  $state,
  $isolation,
}: {
  $state: FlowPath['state']
  $isolation: boolean
}): string {
  return flowStrokeColor($state, $isolation)
}
const flow = keyframes`
  from { stroke-dashoffset: 0; }
  to { stroke-dashoffset: calc(-16 * var(--flow-image-unit, 1px)); }
`
const Layer = styled.g`
  pointer-events: none;
  &[data-flow-running='true'] [data-flow-animated='true'] {
    animation-play-state: running;
  }
`
const FlowRail = styled.path`
  fill: none;
  stroke: var(--color-white);
  stroke-width: calc(5.5 * var(--flow-image-unit, 1px));
  stroke-linecap: round;
  stroke-linejoin: round;
`
const DirectionMarker = styled.path<{
  $state: FlowPath['state']
  $isolation: boolean
}>`
  fill: none;
  stroke: ${strokeColor};
  stroke-opacity: 0;
  stroke-width: calc(3.5 * var(--flow-image-unit, 1px));
`
const Stroke = styled.path<{
  $state: FlowPath['state']
  $isolation: boolean
}>`
  fill: none;
  stroke: ${strokeColor};
  stroke-width: calc(3.5 * var(--flow-image-unit, 1px));
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dashoffset: 0;
  &[data-flow-state='unknown'] {
    stroke-dasharray: calc(3 * var(--flow-image-unit, 1px))
      calc(5 * var(--flow-image-unit, 1px));
  }
  &[data-flow-animated='true'] {
    stroke-dasharray: calc(8 * var(--flow-image-unit, 1px))
      calc(8 * var(--flow-image-unit, 1px));
    animation: ${flow} 900ms linear infinite;
    animation-play-state: paused;
  }
  @media (prefers-reduced-motion: reduce) {
    &[data-flow-animated='true'] {
      stroke-dasharray: none;
      animation: none;
    }
  }
`

function pathData(path: FlowPath): string {
  const points = path.geometry.points
    .map(
      (point, index) =>
        `${index ? 'L' : 'M'}${point.denormalized_x},${point.denormalized_y}`
    )
    .join(' ')
  return path.geometry.type === 'polygon' ? `${points} Z` : points
}

function markerPathData(path: FlowPath): string | undefined {
  const points = path.geometry.points
  const end = points.at(-1)
  if (!end) return undefined
  for (let index = points.length - 2; index >= 0; index--) {
    const start = points[index]
    if (!start) continue
    if (
      start.denormalized_x !== end.denormalized_x ||
      start.denormalized_y !== end.denormalized_y
    )
      return pathData({
        ...path,
        geometry: { type: 'polyline', points: [start, end] },
      })
  }
  return undefined
}

const FlowShapes = memo(function FlowShapes({
  paths,
  isolating,
}: Pick<FlowAnimationLayerProps, 'paths' | 'isolating'>): ReactElement {
  const arrowId = useId()
  const rails: string[] = []
  const directedPaths: {
    id: string
    d: string
    state: FlowPath['state']
  }[] = []
  const groups = new Map<
    string,
    {
      state: FlowPath['state']
      directed: boolean
      segments: string[]
    }
  >()
  for (const path of paths) {
    const d = pathData(path)
    if (path.state === 'enabled') rails.push(d)
    const markerD = path.directed ? markerPathData(path) : undefined
    if (markerD)
      directedPaths.push({ id: path.overlayId, d: markerD, state: path.state })
    const key = `${path.state}:${path.directed}`
    const group = groups.get(key)
    if (group) group.segments.push(d)
    else
      groups.set(key, {
        state: path.state,
        directed: path.directed,
        segments: [d],
      })
  }
  return (
    <>
      <defs>
        <marker
          id={arrowId}
          viewBox='0 0 6 6'
          refX='5'
          refY='3'
          markerWidth='2'
          markerHeight='2'
          orient='auto'>
          <path d='M0 0 L6 3 L0 6 Z' fill='context-stroke' />
        </marker>
      </defs>
      {rails.length > 0 && <FlowRail d={rails.join(' ')} />}
      {[...groups]
        .sort(([, left], [, right]) => {
          const order = { enabled: 0, unknown: 1, blocked: 2 }
          return order[left.state] - order[right.state]
        })
        .map(([key, group]) => (
          <Stroke
            key={key}
            d={group.segments.join(' ')}
            $state={group.state}
            $isolation={Boolean(isolating)}
            data-flow-state={group.state}
            data-flow-animated={group.directed && group.state === 'enabled'}
            data-flow-path-count={group.segments.length}
          />
        ))}
      {directedPaths.map(({ id, d, state }) => (
        <DirectionMarker
          key={id}
          d={d}
          $state={state}
          $isolation={Boolean(isolating)}
          markerEnd={`url(#${arrowId})`}
        />
      ))}
    </>
  )
})

export const FlowAnimationLayer = memo(function FlowAnimationLayer({
  paths,
  isolating,
  markers = noMarkers,
  highlights = noHighlights,
}: FlowAnimationLayerProps): ReactElement {
  const ref = useRef<SVGGElement>(null)
  const animated = useMemo(
    () => paths.some((path) => path.directed && path.state === 'enabled'),
    [paths]
  )
  useEffect(() => {
    const root = ref.current
    if (!root || !animated) return
    let visible = false
    const sync = (): void => {
      root.dataset.flowRunning = String(visible && !document.hidden)
    }
    const observer = new IntersectionObserver((entries) => {
      visible = entries.some((entry) => entry.isIntersecting)
      sync()
    })
    observer.observe(root)
    document.addEventListener('visibilitychange', sync)
    return (): void => {
      observer.disconnect()
      root.dataset.flowRunning = 'false'
      document.removeEventListener('visibilitychange', sync)
    }
  }, [animated])
  return (
    <Layer ref={ref} aria-hidden data-flow-animation={animated}>
      <FlowShapes paths={paths} isolating={isolating} />
      {(markers.length > 0 || highlights.length > 0) && (
        <IsolationMarkerLayer markers={markers} highlights={highlights} />
      )}
    </Layer>
  )
})
